import httpClient from './httpClient';
import objectToFormData from './objectToFormData';
import { stringify } from 'qs';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

function createFormData(data) {
  const { file, imgFile, documentFile, homeworkFile, audioFile, ...rest } = data;
  const formData = objectToFormData(rest);
  if (file) {
    formData.append('file', file.rawFile);
  }
  if (imgFile) {
    formData.append('imgFile', imgFile.rawFile);
  }
  if (documentFile) {
    formData.append('documentFile', documentFile.rawFile);
  }
  if (homeworkFile) {
    formData.append('homeworkFile', homeworkFile.rawFile);
  }
  if (audioFile) {
    formData.append('audioFile', audioFile.rawFile);
  }
  return formData;
}

const dataProvider = {
  getList: (resource, params) => {
    if (resource === 'watchHistory') {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const userId = params.filter.userId;
      const query = {
        sort: [field, order],
        page: page - 1,
        perPage,
      };
      const url = `${apiUrl}/users/${userId}/watch-history?${stringify(query)}`;
      return httpClient(url).then(({ json }) => ({
        data: Array.isArray(json) ? json : [],
        total: Array.isArray(json) ? json.length : 0,
      }));
    }

    if (resource === 'freeTrials') {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const {
        userId,
        phoneNumber,
        isUnitelTrial,
        active,
        expired,
        startFrom,
        startTo,
        expireFrom,
        expireTo,
      } = params.filter || {};

      const base = `${apiUrl}/freeTrials`;
      const query = {
        sort: [field, order],
        page: page - 1,
        perPage,
        // flatten filters for backend compatibility
        ...(userId ? { userId: Number(userId) } : {}),
        ...(phoneNumber ? { phoneNumber } : {}),
        ...(typeof isUnitelTrial !== 'undefined' ? { isUnitelTrial } : {}),
        ...(typeof active !== 'undefined' ? { active } : {}),
        ...(typeof expired !== 'undefined' ? { expired } : {}),
        // Format dates to ISO string for backend compatibility
        ...(startFrom ? { startFrom: new Date(startFrom).toISOString() } : {}),
        ...(startTo ? { startTo: new Date(startTo).toISOString() } : {}),
        ...(expireFrom ? { expireFrom: new Date(expireFrom).toISOString() } : {}),
        ...(expireTo ? { expireTo: new Date(expireTo).toISOString() } : {}),
      };

      const url = `${base}?${stringify(query)}`;

      return httpClient(url).then(({ headers, json }) => ({
        data: Array.isArray(json) ? json : (json?.data || []),
        total: headers?.get('content-range') 
          ? parseInt(headers.get('content-range').split('/').pop(), 10)
          : Array.isArray(json) ? json.length : (json?.data ? json.data.length : 0),
        todayCount: json?.todayCount || 0,
      }));
    }

     if (resource === 'userNotifications') {

      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const userId = params.filter.userId;
      const query = {
        sort: [field, order],
        page: page - 1,
        perPage,
      };
      const url = `${apiUrl}/users/${userId}/notifications?${stringify(query)}`;

      return httpClient(url).then(({ json }) => ({
        data: Array.isArray(json) ? json : [],
        total: Array.isArray(json) ? json.length : 0,
      }));
    }

    if (resource === 'subscriptionHistory') {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const userId = params.filter.userId;
      const query = {
        sort: [field, order],
        page: page - 1,
        perPage,
      };
      const url = `${apiUrl}/users/${userId}/subscription-history?${stringify(query)}`;
      return httpClient(url).then(({ json }) => ({
        data: Array.isArray(json) ? json : [],
        total: Array.isArray(json) ? json.length : 0,
      }));
    }

    if (resource === 'userDevices') {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const userId = params.filter.userId;
      const query = {
        sort: [field, order],
        page: page - 1,
        perPage,
      };
      const url = `${apiUrl}/users/${userId}/devices?${stringify(query)}`;
      return httpClient(url).then(({ json, headers }) => ({
        data: Array.isArray(json) ? json : [],
        total: headers
          ? parseInt(headers.get('content-range').split('/').pop(), 10)
          : Array.isArray(json) ? json.length : 0,
      }));
    }

    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      sort: [field, order],
      page: page - 1,
      perPage,
      filter: params.filter,
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;

    return httpClient(url).then(({ headers, json }) => ({
      data: json,
      total: parseInt(
        headers.get('content-range').split('/').pop(),
        10,
      ),
    }));
  },

  importCSV: (resource, params) => {
    const url = `${apiUrl}/${resource}/csv`;
    return httpClient(url, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data:json }));
  },

  getOne: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
      data: json,
    })),

  get: (resource) =>
    httpClient(`${apiUrl}/${resource}`).then(({ json }) => ({
      data: json,
    })),

  getMany: (resource, params) => {
    const query = {
      filter: { id: params.ids },
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;
    return httpClient(url).then(({ json }) => ({ data: json }));
  },

  getManyReference: (resource, params) => {
    if (resource === 'watchHistory') {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const userId = params.filter?.userId ?? params.id;
      const query = {
        sort: [field, order],
        page: page - 1,
        perPage,
      };
      const url = `${apiUrl}/users/${userId}/watch-history?${stringify(query)}`;
      return httpClient(url).then(({ json }) => {
        return {
          data: Array.isArray(json) ? json : [],
          total: Array.isArray(json) ? json.length : 0,
        };
      });
    }

    if (resource === 'freeTrials') {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const userId = params.filter?.userId ?? params.id;
      const query = {
        sort: [field, order],
        page: page - 1,
        perPage,
      };
      const url = `${apiUrl}/users/${userId}/freeTrials?${stringify(query)}`;
      return httpClient(url).then(({ json }) => ({
        data: Array.isArray(json) ? json : (json?.data || []),
        total: Array.isArray(json) ? json.length : (json?.data ? json.data.length : 0),
      }));
    }
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      sort: [field, order],
      page: page - 1,
      perPage,
      filter: {
        ...params.filter,
        [params.target]: params.id,
      },
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;
    return httpClient(url).then(({ headers, json }) => ({
      data: json,
      total: parseInt(
        headers.get('content-range').split('/').pop(),
        10
      ),
    }));
  },

  update: (resource, params) => {
    if (params.data.file || params.data.imgFile || params.data.documentFile || params.data.homeworkFile || params.data.audioFile) {
      const fd = createFormData(params.data);

      return httpClient(`${apiUrl}/${resource}/${params.id}`, {
        method: 'PUT',
        body: fd,
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }));
  },

  updateMany: (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }));
  },

  patch: (resource, params) => {
    return httpClient(`${apiUrl}/${resource}`, {
      method: 'PATCH',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }));
  },

  create: (resource, params) => {

    if (params.data.file || params.data.imgFile || params.data.documentFile || params.data.homeworkFile || params.data.audioFile) {
      const fd = createFormData(params.data);
      return httpClient(`${apiUrl}/${resource}`, {
        method: 'POST',
        body: fd,
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }
    return httpClient(`${apiUrl}/${resource}`, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({
      data: { ...params.data, id: Array.isArray(json) ? json[0]?.id : json.id },
    }));
  },

  delete: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'DELETE',
    }).then(({ json }) => ({ data: json })),

  deleteMany: (resource, params) => {
    const query = { id: params.ids };
    return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: 'DELETE',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }));
  },
  post: (resource, params) => {
    const { data = {} } = params;
    return httpClient(`${apiUrl}/${resource}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(({ json }) => json);
  },
};

export default dataProvider;